import { SITE } from "./site";

export function organizationJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: SITE.legalName,
    legalName: SITE.legalName,
    url: SITE.url,
    logo: `${SITE.url}${SITE.logoPath}`,
    description: SITE.defaultDescription,
    foundingDate: SITE.founded,
    founder: {
      "@type": "Person",
      name: SITE.founder.name,
      sameAs: [SITE.founder.linkedin],
    },
    address: SITE.addresses.map((a) => ({
      "@type": "PostalAddress",
      addressLocality: a.city,
      addressRegion: a.region,
      addressCountry: a.country,
    })),
    sameAs: [SITE.social.linkedin, SITE.social.twitter, SITE.social.instagram],
    brand: {
      "@type": "Brand",
      name: SITE.name,
      url: SITE.url,
    },
  };
}

export function websiteJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: SITE.name,
    url: SITE.url,
    publisher: { "@type": "Organization", name: SITE.legalName },
  };
}

export function softwareApplicationJsonLd(opts: {
  name: string;
  description: string;
  url: string;
  category?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    applicationCategory: opts.category ?? "BusinessApplication",
    name: opts.name,
    description: opts.description,
    url: opts.url,
    operatingSystem: "Web, Windows, macOS",
    publisher: { "@type": "Organization", name: SITE.legalName },
  };
}

export function faqPageJsonLd(items: { q: string; a: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((it) => ({
      "@type": "Question",
      name: it.q,
      acceptedAnswer: { "@type": "Answer", text: it.a },
    })),
  };
}

export function breadcrumbJsonLd(items: { name: string; url: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: it.url,
    })),
  };
}

export function articleJsonLd(opts: {
  headline: string;
  description: string;
  url: string;
  image?: string;
  datePublished?: string;
  dateModified?: string;
  authorName?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: opts.headline,
    description: opts.description,
    url: opts.url,
    mainEntityOfPage: { "@type": "WebPage", "@id": opts.url },
    image: opts.image,
    datePublished: opts.datePublished,
    dateModified: opts.dateModified ?? opts.datePublished,
    author: opts.authorName
      ? { "@type": "Person", name: opts.authorName }
      : { "@type": "Organization", name: SITE.legalName },
    publisher: {
      "@type": "Organization",
      name: SITE.legalName,
      logo: { "@type": "ImageObject", url: `${SITE.url}${SITE.logoPath}` },
    },
  };
}

export function aboutPageJsonLd(opts: { url: string; description: string }) {
  return {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    url: opts.url,
    name: `About ${SITE.legalName}`,
    description: opts.description,
    publisher: { "@type": "Organization", name: SITE.legalName, url: SITE.url },
  };
}

export function collectionPageJsonLd(opts: {
  url: string;
  name: string;
  description: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    url: opts.url,
    name: opts.name,
    description: opts.description,
    publisher: { "@type": "Organization", name: SITE.legalName, url: SITE.url },
  };
}

export function contactPageJsonLd(opts: {
  url: string;
  name: string;
  description: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    url: opts.url,
    name: opts.name,
    description: opts.description,
    publisher: { "@type": "Organization", name: SITE.legalName, url: SITE.url },
  };
}

export function personJsonLd(opts: {
  name: string;
  jobTitle?: string;
  url?: string;
  image?: string;
  sameAs?: string[];
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Person",
    name: opts.name,
    jobTitle: opts.jobTitle,
    url: opts.url,
    image: opts.image,
    sameAs: opts.sameAs,
    worksFor: { "@type": "Organization", name: SITE.legalName },
  };
}
